/**
 * Auth Composable
 * 認證流程管理
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/auth/auth.js)
 * - 單例模式：使用 nuxtApp._authContext 避免 storeToRefs/computed/watch 重複創建
 */

import { storeToRefs } from 'pinia'
import urljoin from 'url-join'
import { ref, computed, watch, nextTick } from 'vue'

import { authApi, userApi } from '@/api'
import { useAuthStore, AUTH_STATE } from '@/stores/auth/auth'
import { useMainStore } from '@/stores/main'
import { useErrorManagerStore } from '@/stores/ui/errorManager'
import { useSignUpStore } from '@/stores/user/signUp'
import { resolveReferralAc, resolveReferralFc } from '@/utils/referral'
import { storage } from '@/utils/storage'
import { getOriginNoTrailingDot } from '@/utils/url'
import { isAdult } from '@/utils/validationSchemas'

// 輪詢/回前景探測命中這些碼即視為「需登出」（提到模組層以利單元測試斷言集合關係）
export const POLL_LOGOUT_ERROR_CODES = ['ERR_A101', 'ERR_A201', 'ERR_A204', 'ERR_A103', 'ERR_A104', 'ERR_A108', 'ERR_F108', 'ERR_A113']
// 僅「token 失效類」錯誤才自動登入；重複登入(ERR_A204)、帳號不存在(ERR_A201)、權限/維護等仍照舊登出。
// ERR_A113(USER_OFFLINE)：後端超過 5 分鐘沒收到 /self 會把 token 從 Redis 移除並回此碼，
// 是「退背景閒置過期」的實際錯誤碼（ERR_A108 為 token 與 Redis 不匹配），故必須納入自動登入
export const AUTO_LOGIN_ERROR_CODES = ['ERR_A108', 'ERR_A101', 'ERR_A103', 'ERR_A113']
// 硬登出碼（POLL_LOGOUT − AUTO_LOGIN）：不可自動登入、回前景也救不回（token 已被刪，reviveSession 只會拿到
// ERR_A108→誤觸自動登入互踢循環），故須無論前景/背景立即走標準登出管線，不被 isBackgroundEpisode 延後抑制。
export const HARD_LOGOUT_ERROR_CODES = POLL_LOGOUT_ERROR_CODES.filter((code) => !AUTO_LOGIN_ERROR_CODES.includes(code))
// 不該送給後端的值：storage 取回可能是字面字串 'null' / 'undefined'，空字串則會被請求層的 pickBy 保留。
// 第三方轉導 query 與 telegram 登入參數共用這份契約，避免兩條路徑各自維護一份而行為分歧。
const INVALID_QUERY_VALUES = [undefined, null, '', 'undefined', 'null']
export const isFilledQueryValue = (value) => !INVALID_QUERY_VALUES.includes(value)

export const useAuth = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 storeToRefs、computed 和 watch 只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._authContext) {
    const authStore = useAuthStore()
    const mainStore = useMainStore()
    const signUpStore = useSignUpStore()
    const { $request, $getDeviceToken, $bridge } = nuxtApp
    const { apiUrl, clientId } = useRuntimeConfig().public
    const { dtVerify } = useSite()
    const authToken = useAuthToken()
    const { getUserInfo, setUserInfo, fetchUserInfo: userInfoFetchUserInfo } = useUserInfo({ autoInit: false })
    const { token, isLoggedIn, setToken, extendToken, clearToken } = authToken
    const { reset } = useAuthOtp()
    // 自動登入機制全為 client 端（visibility/cookie/回前景登入），SSR 不需也不應初始化：
    // 1) useAutoLogin 於 SSR 建立 useCookie 會註冊 app:rendered 寫回 hook，在 ssr-cleanup 清掉 vueApp 後觸發 callWithNuxt 報錯
    // 2) errorStore 僅供 client 端 runAutoLogin 顯示登入錯誤（純 Pinia，不含 useI18n）
    const autoLogin = import.meta.client ? useAutoLogin() : null
    const errorStore = import.meta.client ? useErrorManagerStore() : null
    const { setModal, setLoading, closeAllModals } = mainStore
    const { setAutoLoggingIn, setRevivingSession, setBackgroundEpisode } = authStore
    const localePath = useLocalePath()
    const route = useRoute()

    // 從 authStore 解構狀態 - 只調用一次
    const authStoreRefs = storeToRefs(authStore)
    const { authState, isPlaying, loginData, deviceTokenChecked, isAutoLoggingIn, isRevivingSession, isBackgroundEpisode } = authStoreRefs

    // SSR-safe: 將 pollingTimer 存儲在 nuxtApp context 而非 Pinia store
    // 這確保 timer ID 不會在 SSR 請求之間共享
    if (!nuxtApp._pollingTimer) {
      nuxtApp._pollingTimer = { value: null }
    }
    const pollingTimer = nuxtApp._pollingTimer

    // SSR-safe: keepalive 防抖時間戳存在 nuxtApp context，避免 SSR 請求間共享
    if (!nuxtApp._lastKeepaliveAt) {
      nuxtApp._lastKeepaliveAt = { value: 0 }
    }
    const lastKeepaliveAt = nuxtApp._lastKeepaliveAt

    const KEEPALIVE_DEBOUNCE_MS = 5000

    // 保存 watch stop 函數，用於 cleanup 時停止所有 watch
    const stopWatchers = []

    // 保留在 composable 的本地狀態（業務邏輯相關）
    const tick = ref(2000)

    // computed - 只創建一次
    const isInline = computed(() => getUserInfo.value?.rank >= 0)
    const isNotInline = computed(() => !(getUserInfo.value?.rank >= 0))
    const isAuthenticated = computed(
      () =>
        authState.value === AUTH_STATE.AUTHENTICATED ||
        authState.value === AUTH_STATE.LOGIN_SUCCESS ||
        authState.value === AUTH_STATE.TOKEN_AUTHENTICATED ||
        authState.value === AUTH_STATE.INLINE ||
        authState.value === AUTH_STATE.LOGIN
    )

    const fetchUserInfo = async () => {
      try {
        const userData = await userInfoFetchUserInfo()

        if (nuxtApp.$sentry) {
          nuxtApp.$sentry.setUser({ id: userData.uid_site })
        }

        return userData
      } catch (error) {
        console.error('[useAuth] fetchUserInfo error:', error)
        throw error
      }
    }

    const checkDeviceToken = async () => {
      try {
        if (!dtVerify.value) {
          return
        }

        const { exist } = await $request.send(userApi.checkDt())
        if (!exist) {
          const dt = await $getDeviceToken()

          if (dt) {
            if (nuxtApp.$sentry) {
              nuxtApp.$sentry.setTag('dt', dt)
            }
            await $request.send(userApi.updateDt({ dt }))
          }
        }
      } catch (error) {
        console.error('[useAuth] checkDeviceToken error:', error)
        throw error
      }
    }

    const authenticate = async (data) => {
      try {
        const authData = { ...loginData.value, ...data }
        const response = await $request.send(authApi.login(authData))

        setToken(response.token)

        return response.token
      } catch (error) {
        console.error('[useAuth] authenticate error:', error)
        clearToken()
        authState.value = AUTH_STATE.IDLE
        throw error
      }
    }

    const authenticateApp = async (data) => {
      try {
        const response = await $request.send(authApi.loginApp(data))

        setToken(response.token)

        return response.token
      } catch (error) {
        console.error('[useAuth] authenticateApp error:', error)
        clearToken()
        authState.value = AUTH_STATE.IDLE
        throw error
      }
    }

    const stopPolling = () => {
      if (!import.meta.client) {
        return
      }

      if (pollingTimer.value) {
        clearInterval(pollingTimer.value)
        pollingTimer.value = null
      }

      if (nuxtApp._pollVisibilityHandler) {
        document.removeEventListener('visibilitychange', nuxtApp._pollVisibilityHandler)
        nuxtApp._pollVisibilityHandler = null
      }
      if (nuxtApp._pollFocusHandler) {
        window.removeEventListener('focus', nuxtApp._pollFocusHandler)
        nuxtApp._pollFocusHandler = null
      }
      if (nuxtApp._pollOnlineHandler) {
        window.removeEventListener('online', nuxtApp._pollOnlineHandler)
        nuxtApp._pollOnlineHandler = null
      }
      if (nuxtApp._pollPageShowHandler) {
        window.removeEventListener('pageshow', nuxtApp._pollPageShowHandler)
        nuxtApp._pollPageShowHandler = null
      }
      if (nuxtApp._autoLoginHiddenHandler) {
        document.removeEventListener('visibilitychange', nuxtApp._autoLoginHiddenHandler)
        nuxtApp._autoLoginHiddenHandler = null
      }
      if (nuxtApp._autoLoginPageHideHandler) {
        window.removeEventListener('pagehide', nuxtApp._autoLoginPageHideHandler)
        nuxtApp._autoLoginPageHideHandler = null
      }
    }

    // skipRemote：呼叫端已確知 token 在伺服器端失效（AUTO_LOGIN_ERROR_CODES 類），
    // 再 POST /logout 只會多產生一筆 401，故僅做本地清理
    const logout = async ({ skipRemote = false } = {}) => {
      try {
        if (!skipRemote && token.value) {
          await $request.send(authApi.logout())
        }
      } catch (error) {
        console.error('[useAuth] logout error:', error)
      } finally {
        if ($bridge?.available) {
          $bridge.callHandler('resetAuth', null, () => {
            console.log('[Bridge] Auth Reset')
          })
        }
        reset()
        signUpStore.resetState()
        clearToken()
        autoLogin?.clearCredentials()
        setUserInfo({})
        authState.value = AUTH_STATE.IDLE
        deviceTokenChecked.value = false
        setBackgroundEpisode(false)
        setModal(null)
        stopPolling()
      }
    }

    // auto-login 最終失敗時顯式導回首頁：authenticate() 早已把 authState 設為 IDLE（彼時 isAutoLoggingIn 仍為
    // true，default.vue 的 idle 導頁 watcher 被守衛擋下），其後 logout() 再設 IDLE 值未變、watcher 不會二次觸發，
    // 故需在此補導，避免卡在 /main。對齊 watcher 語意：僅在 /main(含子路由) 時才導回首頁。
    const redirectHomeFromMain = () => {
      if (!import.meta.client) {
        return
      }
      const mainPath = localePath('/main')
      if (route.path === mainPath || route.path.startsWith(`${mainPath}/`)) {
        navigateTo(localePath('/'))
      }
    }

    const startPolling = () => {
      if (!import.meta.client) {
        return
      }

      stopPolling()

      const pollInterval = tick.value * (isPlaying.value ? 5 : 1)

      // 自動登入：偵測到 token 過期時，用 cookie 內帳密無感重新登入。
      // 定義於下方 if 區塊外，讓 reviveSession（回前景）與輪詢 setInterval 皆能呼叫同一函式（共用互斥鎖）。
      const runAutoLogin = async () => {
        if (isAutoLoggingIn.value) {
          return
        }
        // 同步在進入點即設旗標（須早於任何 await），讓輪詢與 reviveSession 共用同一互斥鎖，
        // 避免回前景時兩者在各自 await 前都判為 false 而競態雙跑（TOCTOU）
        setAutoLoggingIn(true)
        let creds
        try {
          creds = await autoLogin.readCredentials()
        } catch {
          creds = null
        }
        if (!creds) {
          // cookie 解密失敗等無法恢復：清旗標後照舊登出（不顯示登入錯誤彈窗）
          setAutoLoggingIn(false)
          stopPolling()
          await logout()
          return
        }
        setLoading({ autoLogin: true })
        try {
          await loginWith(creds)
          // 成功：清掉持久化的 cred cookie（保留記憶體密文，下次退背景由 saveCredentialCookie 重新寫回）
          autoLogin.clearCredentialCookie()
        } catch (error) {
          // 最終失敗：先清旗標，logout 後顯式導回首頁，最後寫 error store 顯示登入錯誤
          // （authState 在 authenticate() 已先被設為 IDLE，idle 導頁 watcher 因彼時旗標仍亮被擋下，
          //   logout() 再設 IDLE 不會二次觸發，故此處改為顯式 redirectHomeFromMain 補導）
          // （在 logout 的 setModal(null) 之後設定 errorType，彈窗不被關掉）（案例 #9 / #10）
          setAutoLoggingIn(false)
          await logout()
          redirectHomeFromMain()
          // logout 的 setModal(null) 只關主 modal，這裡關掉其餘所有 modal（保留 error modal）
          // 須在 errorStore.setError 之前呼叫，避免把接著要顯示的登入錯誤彈窗一併關掉（對齊 default.vue closeAllModals 用法）
          closeAllModals()
          errorStore?.setError(error)
          errorStore?.setErrorType('error')
        } finally {
          setLoading({ autoLogin: false })
          // 背景 episode 至此已落定結局（成功維持登入 / 失敗已 logout），統一清旗標
          setBackgroundEpisode(false)
          // 成功路徑在此清旗標；失敗/無帳密路徑已先清，故此處僅補成功情況
          if (isAutoLoggingIn.value) {
            setAutoLoggingIn(false)
          }
        }
      }

      // 背景→前景時主動打一次 /self 重新驗證後端 session。
      // 只搬 cookie（extendToken）碰不到後端、救不回已過期的 token，所以這裡必須真的打 /self。
      // 涵蓋三種回前景事件：visibilitychange、window focus、pageshow.persisted（iOS bfcache 還原）。
      if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        const reviveSession = async (reason) => {
          if (isAutoLoggingIn.value) {
            return
          }
          if (!isLoggedIn.value) {
            // token cookie ref 已歸零（背景中 maxAge 到期被 Nuxt cookieRef 清空，或他處登出）
            // 非背景 episode（例如他處主動 logout、開站半登出殘留）→ 維持原半登出語意，不自動登入
            if (!isBackgroundEpisode.value) {
              return
            }
            // 背景 episode 但回前景時仍離線 → 先不裁決、保留 episode，待 online 事件再觸發 reviveSession
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
              return
            }
            // 背景 episode + 網路正常：有帳密 → 無感自動登入復原（此時 isLoggedIn=false 打不了 /self，直接重登）；
            // 無帳密 → 標準登出。兩條路皆會清 isBackgroundEpisode（runAutoLogin / logout 的 finally）
            if (autoLogin.hasStoredCredentials()) {
              await runAutoLogin()
            } else {
              setBackgroundEpisode(false)
              await logout()
            }
            return
          }
          if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return
          }
          // 防抖：visibilitychange 與 focus 常會連續觸發，5 秒內只重新驗證一次
          const now = Date.now()
          if (now - lastKeepaliveAt.value < KEEPALIVE_DEBOUNCE_MS) {
            // 5 秒內剛裁決過 session，本次回前景不需再延後；不清會讓旗標永久卡住而持續抑制錯誤彈窗
            setBackgroundEpisode(false)
            return
          }
          lastKeepaliveAt.value = now

          // 先續一次 cookie，避免 /self 還沒回來前 cookie 先到期
          extendToken()

          // 探測窗：在打 /self 之前同步開啟（早於 runAutoLogin 才設的 isAutoLoggingIn），
          // 讓探測 /self 失敗、_checkToken A103、並行輪詢的錯誤彈窗一律被抑制；finally 確保旗標必清不卡死
          setRevivingSession(true)
          try {
            await fetchUserInfo()
            extendToken()
          } catch (error) {
            console.error(`[useAuth] keepalive (${reason}) error:`, error)
            // await 後重檢：輪詢的背景在飛請求可能已搶先啟動自動登入
            if (isAutoLoggingIn.value) {
              return
            }
            if (POLL_LOGOUT_ERROR_CODES.includes(error?.code)) {
              // 回前景偵測到 token 失效且有儲存帳密 → 無感自動登入（唯一允許靜默處理的情境）
              // 不在此清 reviving：交棒給 runAutoLogin 進入點同步設的 isAutoLoggingIn 無縫接續抑制，finally 才清
              if (AUTO_LOGIN_ERROR_CODES.includes(error?.code) && autoLogin.hasStoredCredentials()) {
                await runAutoLogin()
                return
              }
              // 無帳密或其他登出類錯誤碼 → 走標準錯誤管線：
              // errorType='logout' 由 default.vue watcher 顯示 ErrorHandler、送 GA error_event 並執行全域登出
              // 此分支須讓彈窗顯示，故在 setError 前先清 reviving 與 background episode
              // （finally 仍會再清一次 episode，冪等；提前清是為了讓守衛在 errorType watcher flush 前即放行）
              setRevivingSession(false)
              setBackgroundEpisode(false)
              stopPolling()
              errorStore?.setError(error)
              errorStore?.setErrorType('logout')
            }
            // 其他錯誤（網路中斷等）交給正常輪詢重試，不在這裡登出
          } finally {
            setRevivingSession(false)
            // 回前景已裁決完畢（/self 成功維持登入 / 交棒 runAutoLogin / 標準登出）→ 結束背景 episode。
            // 註：onLine===false 早退發生在 try 之前、不進此 finally，故 episode 會保留等 online 事件再裁決
            setBackgroundEpisode(false)
          }
        }

        const handleVisibilityVisible = () => {
          if (document.visibilityState !== 'visible') {
            return
          }
          reviveSession('visibilitychange')
        }
        const handleWindowFocus = () => reviveSession('focus')
        const handlePageShow = (event) => {
          if (event.persisted) {
            reviveSession('pageshow.persisted')
          }
        }
        // 網路恢復：補裁決「回前景時仍離線」的背景 episode（reviveSession 當時於 onLine 早退、未清 episode）
        const handleOnline = () => reviveSession('online')

        // 存帳密 listener 與 polling 共用生命週期：polling 只在已登入時運行，正好是需要存帳密的時機；
        // 隨 stopPolling 一併移除（見上方 stopPolling），故毋須額外清理路徑
        // 退背景同時標記 isBackgroundEpisode（僅已登入時），讓背景期間的 session 流失訊號延後交給回前景裁決
        const handleHidden = () => {
          if (document.visibilityState === 'hidden') {
            if (isLoggedIn.value) {
              setBackgroundEpisode(true)
            }
            autoLogin.saveCredentialCookie()
          }
        }
        const handlePageHide = () => {
          if (isLoggedIn.value) {
            setBackgroundEpisode(true)
          }
          autoLogin.saveCredentialCookie()
        }

        nuxtApp._pollVisibilityHandler = handleVisibilityVisible
        nuxtApp._pollFocusHandler = handleWindowFocus
        nuxtApp._pollPageShowHandler = handlePageShow
        nuxtApp._pollOnlineHandler = handleOnline
        nuxtApp._autoLoginHiddenHandler = handleHidden
        nuxtApp._autoLoginPageHideHandler = handlePageHide
        document.addEventListener('visibilitychange', handleVisibilityVisible)
        window.addEventListener('focus', handleWindowFocus)
        window.addEventListener('pageshow', handlePageShow)
        window.addEventListener('online', handleOnline)
        document.addEventListener('visibilitychange', handleHidden)
        window.addEventListener('pagehide', handlePageHide)
      }

      pollingTimer.value = setInterval(async () => {
        // 探測窗期間（reviving）讓位給 reviveSession，避免並行輪詢重複觸發/誤彈窗
        if (isAutoLoggingIn.value || isRevivingSession.value) {
          return
        }
        if (!isLoggedIn.value) {
          // 背景 episode 期間 token ref 被 cookieRef 歸零：保留 listener、不拆除，等回前景的 reviveSession 復原（E 安全網）
          if (isBackgroundEpisode.value) {
            return
          }
          stopPolling()
          return
        }
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          // 背景離線：續一次 cookie ref（重置 Nuxt cookieRef 的 maxAge 計時器），避免 token ref 在背景被歸零、
          // 使 isLoggedIn 翻 false 而提前導頁（C 保活）。前景離線維持原樣不續期，讓 token 自然過期（約束 4）
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            extendToken()
          }
          return
        }

        // 記錄「發出當下」的前背景狀態：背景發出的請求可能在回前景後才以失敗 resolve（microtask 比
        // visibilitychange 事件先跑），若只看失敗當下的 visibilityState 會把背景失敗誤判為前景失敗而登出
        const startedHidden = typeof document !== 'undefined' && document.visibilityState !== 'visible'

        try {
          await fetchUserInfo()
          extendToken()
        } catch (error) {
          console.error('[useAuth] polling error:', error)

          // await 後重新檢查：回前景時 reviveSession 可能已搶先啟動自動登入或進入探測窗，避免重複登出/登入
          if (isAutoLoggingIn.value || isRevivingSession.value) {
            return
          }

          if (POLL_LOGOUT_ERROR_CODES.includes(error?.code)) {
            // 僅「背景→前景過渡」（請求發出當下或失敗當下在背景）才無感自動登入（與 reviveSession 對稱）；
            // 前景正常操作中 token 失效（後端重啟、Redis 清除等）與其他登出類錯誤碼，
            // 一律走標準錯誤管線：errorType='logout' 由 default.vue watcher 顯示 ErrorHandler、送 GA 並執行全域登出
            const isBackgroundTransition = startedHidden || (typeof document !== 'undefined' && document.visibilityState !== 'visible')
            if (isBackgroundTransition && AUTO_LOGIN_ERROR_CODES.includes(error?.code) && autoLogin.hasStoredCredentials()) {
              await runAutoLogin()
              return
            }
            // 走標準登出（含背景輪詢命中硬登出碼 ERR_A204 等）：先清 background episode，否則 default.vue 的
            // errorType watcher 守衛（isAutoLoggingIn||isRevivingSession||isBackgroundEpisode）會在背景期間擋下
            // 登出/彈窗，且 errorType 卡在 'logout' 不再重觸發 → 永久卡死（多重登入未登出 bug）。
            setBackgroundEpisode(false)
            stopPolling()
            errorStore?.setError(error)
            errorStore?.setErrorType('logout')
            return
          }

          // 純網路失敗（無後端錯誤碼）：前景與背景一律不計數、不登出，下一個 tick 繼續打 /self。
          // 失敗期間不呼叫 extendToken()，token cookie ref 的 maxAge(300s) 計時器不續期，
          // 最終自然歸零 → isLoggedIn 翻 false → 由下方 watch(isLoggedIn) 執行 clean logout。
          // （背景發出、回前景才 resolve 失敗的請求亦走此路徑，與 reviveSession 樂觀重置語意一致。）
        }
      }, pollInterval)
    }

    // 第三方註冊於 RegisterStep 暫存的生日（redirect query 白名單不帶 birthday），登入完成後補交
    const submitPendingBirthday = async () => {
      const birthday = storage.get('tp_birthday')
      if ([undefined, 'undefined', null, 'null', ''].includes(birthday)) return
      storage.remove('tp_birthday')
      if (getUserInfo.value?.birthday || !isAdult(birthday)) return
      try {
        await $request.send(userApi.updateUserInfo({ birthday }))
        await fetchUserInfo()
      } catch (error) {
        console.error('[useAuth] submitPendingBirthday error:', error)
      }
    }

    const completeLoginFlow = async () => {
      authState.value = AUTH_STATE.AUTHENTICATED
      await fetchUserInfo()
      await submitPendingBirthday()

      authState.value = AUTH_STATE.LOGIN_SUCCESS
      await nextTick()

      if (isInline.value) {
        authState.value = AUTH_STATE.INLINE
      } else {
        authState.value = AUTH_STATE.LOGIN
      }
      startPolling()
    }

    const loginWithToken = async (newToken) => {
      try {
        setBackgroundEpisode(false)
        if (newToken) {
          setToken(newToken)
        }

        if (isLoggedIn.value) {
          authState.value = AUTH_STATE.TOKEN_AUTHENTICATED
          await fetchUserInfo()
          await submitPendingBirthday()
          // extendToken()

          if (isInline.value) {
            authState.value = AUTH_STATE.INLINE
          } else {
            authState.value = AUTH_STATE.LOGIN
          }
          startPolling()
        }
      } catch (error) {
        console.error('[useAuth] loginWithToken error:', error)
        clearToken()
        authState.value = AUTH_STATE.IDLE
        throw error
      }
    }

    // watch - 只創建一次，保存 stop 函數供 cleanup 使用
    stopWatchers.push(
      watch(authState, async (newState) => {
        if (newState === AUTH_STATE.LOGIN && !deviceTokenChecked.value) {
          deviceTokenChecked.value = true
          await checkDeviceToken()
        }
      })
    )

    // Token 自然過期的 clean logout（前景持續網路失敗 → 不 extendToken → maxAge 計時器歸零 token ref）。
    // 以「authState 仍非 IDLE」discriminate：他處 logout() 會同步設 authState=IDLE（早於 Vue watcher 異步 flush），
    // 故由 logout() 造成的翻轉到本 watcher flush 時 authState 已是 IDLE → 守衛擋下，不重入。
    // 自動登入／回前景探測／背景 episode 期間的翻轉一律讓位（與 main.vue / default.vue 守衛對稱）。
    if (import.meta.client) {
      stopWatchers.push(
        watch(isLoggedIn, async (newVal, oldVal) => {
          if (newVal || !oldVal) {
            return
          }
          if (authState.value === AUTH_STATE.IDLE || isAutoLoggingIn.value || isRevivingSession.value || isBackgroundEpisode.value) {
            return
          }
          await logout()
        })
      )
    }

    const loginWith = async (data) => {
      try {
        setBackgroundEpisode(false)
        loginData.value = data
        authState.value = AUTH_STATE.AUTHENTICATING
        await authenticate()
        await completeLoginFlow()
        await autoLogin?.captureCredentials(data)
      } catch (error) {
        console.error(`[useAuth] login (${data.provider}) error:`, error)
        throw error
      }
    }

    const loginWithId = async ({ username, password }) => loginWith({ provider: 'local', username, password })

    const loginWithPhone = async ({ countryCode: code, phone, password }) => loginWith({ provider: 'phone', countryCode: code, phone, password })

    const loginWithEmail = async ({ email, password }) => loginWith({ provider: 'email', email, password })

    const loginWithTelegram = async ({ loginType, additionalUserInfo: addInfo, isSignature, idToken }) => {
      // if (loginType == 'WebApp') {
      //   return loginWith({
      //     provider: 'telegram',
      //     loginType,
      //     additionalUserInfo: addInfo,
      //     isSignature,
      //   })
      // }

      const dt = dtVerify.value ? await $getDeviceToken() : null
      if (dt && nuxtApp.$sentry) {
        nuxtApp.$sentry.setTag('dt', dt)
      }

      const affiliateCode = resolveReferralAc()
      const nickname = storage.get('nk')
      const tpPhone = storage.get('tp_phone')
      const tpCountryCode = storage.get('tp_countryCode')
      const tpEmail = storage.get('tp_email')

      const referralObj = Object.fromEntries(
        Object.entries({
          ac: affiliateCode,
          nick_name: nickname,
          phone: tpPhone,
          countryCode: tpCountryCode,
          email: tpEmail,
        }).filter(([, value]) => isFilledQueryValue(value))
      )

      storage.remove('tp_phone')
      storage.remove('tp_countryCode')
      storage.remove('tp_email')

      return loginWith({
        provider: 'telegram',
        loginType,
        additionalUserInfo: addInfo,
        isSignature,
        idToken,
        dt,
        platform: 'pwa',
        fc: resolveReferralFc(),
        ...referralObj,
      })
    }

    const loginWithApp = async (data) => {
      try {
        setBackgroundEpisode(false)
        authState.value = AUTH_STATE.AUTHENTICATING
        await authenticateApp(data)

        await completeLoginFlow()
      } catch (error) {
        console.error('[useAuth] loginWithApp error:', error)
        throw error
      }
    }

    const redirectTo3Party = async ({ provider, fc: friendCode }) => {
      // 轉導參數只在轉導失敗時才有診斷價值，成功路徑上報會讓每次第三方登入都獨立成案
      // （CLIENT-PWA-SSR-169）；改為留到 catch 內附在該次錯誤事件上
      let redirectDiagnostics = null

      try {
        const dt = dtVerify.value ? await $getDeviceToken() : null

        if (dt && nuxtApp.$sentry) {
          nuxtApp.$sentry.setTag('dt', dt)
        }

        const affiliateCode = resolveReferralAc()
        const nickname = storage.get('nk')
        const phone = storage.get('tp_phone')
        const countryCode = storage.get('tp_countryCode')
        const email = storage.get('tp_email')

        redirectDiagnostics = {
          url: window.location.href,
          affiliateCode,
          nickname,
        }

        storage.remove('tp_phone')
        storage.remove('tp_countryCode')
        storage.remove('tp_email')

        // URLSearchParams 會把 null / undefined 序列化成字面字串 'null' / 'undefined'，
        // 且 storage 取回的值本身就可能是這兩個字串，未過濾會讓後端收到假值
        const query = Object.fromEntries(
          Object.entries({
            provider,
            redirectURI: getOriginNoTrailingDot(),
            ac: affiliateCode,
            nick_name: nickname,
            phone,
            countryCode,
            email,
            dt,
            platform: 'pwa',
            fc: friendCode,
          }).filter(([, value]) => isFilledQueryValue(value))
        )

        window.location.href = urljoin(apiUrl, 'v2', clientId, `login?${new URLSearchParams(query).toString()}`)
      } catch (error) {
        console.error('[useAuth] redirectTo3Party error:', error)

        if (nuxtApp.$sentry) {
          nuxtApp.$sentry.captureException(error, {
            tags: { flow: 'redirectTo3Party', provider },
            extra: redirectDiagnostics ?? {},
          })
        }

        throw error
      }
    }

    const setPlay = (playing) => {
      isPlaying.value = playing
      if (pollingTimer.value) {
        startPolling()
      }
    }

    // cleanup 方法：停止所有 watch 和 polling，供 SSR cleanup plugin 調用
    const cleanup = () => {
      stopWatchers.forEach((stop) => stop?.())
      stopPolling()
    }

    nuxtApp._authContext = {
      authState,
      isPlaying,
      loginData,
      isInline,
      isNotInline,
      isAuthenticated,
      isAutoLoggingIn,
      isRevivingSession,
      isBackgroundEpisode,

      loginWithToken,
      loginWithId,
      loginWithPhone,
      loginWithEmail,
      loginWithTelegram,
      loginWithApp,
      redirectTo3Party,
      logout,
      setPlay,
      fetchUserInfo,
      stopPolling,
      cleanup,
    }
  }

  return nuxtApp._authContext
}
